import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ProductFacets, type ProductQuery, type ProductTypeKey } from "@/api";
import { CatalogFilters } from "@/components/catalog/catalog-filters";
import { XIcon } from "@/components/icons";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PRODUCT_TYPES, selectedProductTypes, withProductTypes } from "@/lib/product-query";

/** Набор фильтров «Моей ленты». Пустые поля означают «без ограничения». */
export type FeedFilters = {
  categoryIds: string[];
  sizes: string[];
  colors: string[];
  priceMin?: number;
  priceMax?: number;
  /** Старый флаг «Только в наличии» (сохранённые ленты до 02.10.2026). Новые
   *  ленты пишут `types`, флаг читается как `types: ["in-stock"]`. */
  inStock: boolean;
  /** Отмеченные чипы «Товар»; пусто или все три — без ограничения. */
  types?: ProductTypeKey[];
  /** Переключатель «SALE». */
  sale?: boolean;
};
export const EMPTY_FEED_FILTERS: FeedFilters = {
  categoryIds: [],
  sizes: [],
  colors: [],
  inStock: false
};

/** Чипы «Товар» ленты с учётом старого флага `inStock`. */
export const feedProductTypes = (f: FeedFilters): ProductTypeKey[] | undefined => {
  const types = f.types?.length ? f.types : f.inStock ? ["in-stock"] as ProductTypeKey[] : undefined;
  return types && types.length < PRODUCT_TYPES.length ? types : undefined;
};
export const hasFeedFilters = (f: FeedFilters): boolean => f.categoryIds.length > 0 || f.sizes.length > 0 || f.colors.length > 0 || f.priceMin != null || f.priceMax != null || feedProductTypes(f) != null || f.sale === true;

/** Фильтры ленты → часть запроса выдачи (без корней раздела — их добавляет главная). */
export const feedQueryOf = (f: FeedFilters): Partial<ProductQuery> => {
  const types = feedProductTypes(f);
  return {
    sizes: f.sizes.length ? f.sizes : undefined,
    colors: f.colors.length ? f.colors : undefined,
    priceMin: f.priceMin,
    priceMax: f.priceMax,
    // Один чип «В наличии» уходит и старым флагом — для шлюза без productType.
    inStock: types?.length === 1 && types[0] === "in-stock" || undefined,
    productTypes: types,
    sale: f.sale || undefined
  };
};

// Фильтры ленты ⇄ параметры панели: панель одна на каталог и ленту и живёт на
// URLSearchParams (у каталога это адрес, у ленты — черновик).
const toParams = (f: FeedFilters): URLSearchParams => {
  const p = new URLSearchParams();
  for (const id of f.categoryIds) p.append("categoryId", id);
  for (const s of f.sizes) p.append("size", s);
  for (const c of f.colors) p.append("color", c);
  if (f.priceMin != null) p.set("priceMin", String(f.priceMin));
  if (f.priceMax != null) p.set("priceMax", String(f.priceMax));
  if (f.sale) p.set("sale", "true");
  return withProductTypes(p, feedProductTypes(f) ?? [...PRODUCT_TYPES]);
};
const fromParams = (p: URLSearchParams): FeedFilters => {
  const num = (k: string) => {
    const v = p.get(k);
    return v != null && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : undefined;
  };
  const types = selectedProductTypes(p);
  return {
    categoryIds: p.getAll("categoryId"),
    sizes: p.getAll("size"),
    colors: p.getAll("color"),
    priceMin: num("priceMin"),
    priceMax: num("priceMax"),
    inStock: false,
    types: types.length < PRODUCT_TYPES.length ? types : undefined,
    sale: p.get("sale") === "true" || undefined
  };
};

/**
 * Настройка «Моей ленты».
 *
 * A-11 (Айталы 02.10): «Настроить ленту — это обычные фильтры с левого края».
 * Вместо правой панели «Настройка ленты» с оранжевыми чипами открывается та же
 * панель «Все фильтры», что в каталоге (кадры 3845:151131 / 3845:151403),
 * слева, 610px. Отличие одно: выбор копится в черновике и сохраняется в ленту
 * кнопкой «Показать N товаров» (лента переживает перезагрузку — preferences),
 * а закрытие крестиком изменений не сохраняет. Категорий можно отметить
 * несколько.
 */
export function FeedSettingsPanel(t0) {
  const $ = _c(59);
  const {
    open,
    onOpenChange,
    value,
    onApply,
    countQuery
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== value) {
    t1 = () => toParams(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [draft, setDraft] = useState(t1);
  let t2;
  if ($[2] !== open || $[3] !== value) {
    t2 = {
      value,
      open
    };
    $[2] = open;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [openedWith, setOpenedWith] = useState(t2);
  if (openedWith.value !== value || openedWith.open !== open) {
    setOpenedWith({
      value,
      open
    });
    if (open) {
      setDraft(toParams(value));
    }
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["facets", "feed"];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== open) {
    t4 = {
      queryKey: t3,
      queryFn: _temp,
      enabled: open
    };
    $[6] = open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const facetsQuery = useQuery(t4);
  let draftFilters;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== countQuery || $[9] !== draft) {
    draftFilters = fromParams(draft);
    t10 = useQuery;
    t5 = "products";
    t6 = "home";
    t7 = "count";
    if ($[17] !== draft) {
      t8 = draft.toString();
      $[17] = draft;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t9 = countQuery(draftFilters);
    $[8] = countQuery;
    $[9] = draft;
    $[10] = draftFilters;
    $[11] = t10;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    draftFilters = $[10];
    t10 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = [t5, t6, t7, t8, t9];
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== countQuery || $[26] !== draftFilters) {
    t12 = () => api.products.list({
      ...countQuery(draftFilters),
      page: 1,
      pageSize: 1
    });
    $[25] = countQuery;
    $[26] = draftFilters;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== open || $[29] !== t11 || $[30] !== t12) {
    t13 = {
      queryKey: t11,
      queryFn: t12,
      enabled: open,
      placeholderData: _temp2
    };
    $[28] = open;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const countQ = t10(t13);
  const facets = facetsQuery.data;
  let t14;
  if ($[32] !== t) {
    t14 = t("filters.allTitle", {
      defaultValue: "\u0412\u0441\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u044B"
    });
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <SheetTitle className="text-[20px] font-semibold leading-[23px] tracking-[-0.31px] text-ds-ink-2">{t14}</SheetTitle>;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t) {
    t16 = t("common.close");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <XIcon />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16) {
    t18 = <SheetClose aria-label={t16} className="flex size-6 shrink-0 items-center justify-center text-ds-ink-2 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t17}</SheetClose>;
    $[39] = t16;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t15 || $[42] !== t18) {
    t19 = <SheetHeader className="flex-row items-center justify-between gap-6 pt-[30px] pr-5 pb-2.5 pl-[30px]">{t15}{t18}</SheetHeader>;
    $[41] = t15;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== countQ || $[45] !== draft || $[46] !== facets || $[47] !== facetsQuery.isPending || $[48] !== onApply || $[49] !== onOpenChange || $[50] !== t) {
    t20 = facetsQuery.isPending ? <p className="px-[30px] py-8 text-[14px] leading-[18px] text-black/50">{t("common.loading", {
        defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026"
      })}</p> : !facets ? <p className="px-[30px] py-8 text-[14px] leading-[18px] text-black/50">{t("common.error", {
        defaultValue: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440\u044B"
      })}</p> : <CatalogFilters facets={facets} params={draft} setParams={setDraft} total={countQ.data?.meta.total} multiCategory={true} onApply={() => {
      onApply(fromParams(draft));
      onOpenChange(false);
    }} />;
    $[44] = countQ;
    $[45] = draft;
    $[46] = facets;
    $[47] = facetsQuery.isPending;
    $[48] = onApply;
    $[49] = onOpenChange;
    $[50] = t;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t19 || $[53] !== t20) {
    t21 = <SheetContent side="left" showCloseButton={false} className="w-full gap-0 border-0 p-0 shadow-none sm:max-w-[610px]">{t19}{t20}</SheetContent>;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== onOpenChange || $[56] !== open || $[57] !== t21) {
    t22 = <Sheet open={open} onOpenChange={onOpenChange}>{t21}</Sheet>;
    $[55] = onOpenChange;
    $[56] = open;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}
function _temp2(prev) {
  return prev;
}
function _temp() {
  return api.products.facets({});
}